/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { BarChart } from "../../models/http/charts/bar-chart/bar-chart.model";
import { AdvancedFilters } from "../../models/http/dashboard/advanced-filters.model";
import { ChartResponse } from "../../models/ui/charts.model";
import { HttpMethods } from "../../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";
import { CommunicationService } from "../communication.service";

@Injectable({
	providedIn: "root"
})
export class DashboardImpactHttpService {
	private _dashboardImpactOperations = ENDPOINT_GROUPS.dashboardImpact.operations;

	constructor(private _communicationService: CommunicationService) {}

	/**
	 * Retrieves 10 worst emissions layers by CO2 bar chart data within the specified date range.
	 *
	 * @param filters - The AdvancedFilters payload as defined in the cursor-based filters API spec.
	 * @returns An Observable that emits the bar chart data.
	 */
	public getWorst10Layers(filters: AdvancedFilters): Observable<ChartResponse<BarChart>> {
		return this._communicationService
			.callService(
				this._dashboardImpactOperations.getWorst10Layers.service,
				HttpMethods.POST,
				{},
				filters
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	/**
	 * Retrieves 10 worst emissions applications by CO2 bar chart data within a specified date range.
	 *
	 * @param filters - The AdvancedFilters payload as defined in the cursor-based filters API spec.
	 * @returns An Observable that emits the bar chart data for the worst emissions applications.
	 */
	public getWorst10Apps(filters: AdvancedFilters): Observable<ChartResponse<BarChart>> {
		return this._communicationService
			.callService(
				this._dashboardImpactOperations.getWorst10Apps.service,
				HttpMethods.POST,
				{},
				filters
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	public downloadAsCsv(startDate: string, endDate: string): Observable<Blob> {
		const params = new HttpParams({
			fromObject: {
				startDate,
				endDate
			}
		});

		return this._communicationService
			.callService(this._dashboardImpactOperations.downloadAsCsv.service, HttpMethods.GET, {
				params,
				responseType: "blob"
			})
			.pipe(catchError(() => of(new Blob())));
	}
}
